import { getCategoryIcon } from './category-icons';
import { LOGGED_IN } from '../default-modules/default-variables';

// ========================================
// DELADE MENY-HELPERS
// ========================================

/**
 * Returnerar NavTree-elementet (#NavTree-Vertikal > ul).
 * En enda plats att ändra selektorn.
 */
export function getNavTree() {
  return document.querySelector('#NavTree-Vertikal > ul');
}

/**
 * Bygger navigeringsmenyn dynamiskt från #MainMenu.
 *
 * Läser hela menystrukturen så att kunden kan ändra menyn själv via CMS.
 * Första MainMenu-elementet med undermeny (menu-has-sub) används som wrapper
 * för produktkategorierna från NavTree.
 * Övriga MainMenu-element (t.ex. Kontakt, Om Entrade) läggs in som egna
 * toppnivå-poster i NavTree.
 */
export function buildNavFromMainMenu(navTree, options = {}) {
  const { className = 'wl-products-menu-item', wrapperClassName = 'wl-menu-products', isMegaMenu = false } = options;

  if (navTree.querySelector(`.${className}`)) return;

  const mainMenu = document.querySelector('#MainMenu > ul');
  if (!mainMenu) return;

  const mainMenuItems = [...mainMenu.querySelectorAll(':scope > li.level1')];
  const productsSource = mainMenuItems.find((item) => item.classList.contains('menu-has-sub'));
  const existingNavItems = [...navTree.querySelectorAll(':scope > li')];

  // Skapa produktmeny om den finns
  if (productsSource) {
    createProductsMenuContainer(navTree, productsSource, existingNavItems, { className, wrapperClassName, isMegaMenu });
  }

  // Lägg till övriga MainMenu-poster
  addOtherMainMenuItems(navTree, mainMenuItems, productsSource, isMegaMenu);

  return navTree.querySelector(`.${className}`);
}

/**
 * Skapar och populerar produktmenyn med alla kategorier och undermenysposter.
 */
function createProductsMenuContainer(navTree, productsSource, existingNavItems, options) {
  const { className, wrapperClassName, isMegaMenu } = options;

  // Hämta label från MainMenu
  const titleSpan = productsSource.querySelector(':scope > span.menu-sub-title');
  const titleLink = productsSource.querySelector(':scope > a');
  const productsLabel = titleSpan?.textContent?.trim() || titleLink?.textContent?.trim();
  if (!productsLabel) return;

  // Skapa huvudelement
  const productMenuItem = document.createElement('li');
  productMenuItem.className = `${className} wl-added`;
  if (isMegaMenu) productMenuItem.classList.add('wl-megamenu-products');

  const menuDiv = document.createElement('div');
  menuDiv.className = wrapperClassName;

  const menuLink = document.createElement('a');
  menuLink.textContent = productsLabel;
  menuLink.href = productsSource.querySelector(':scope > ul > li > a')?.href || '/sv/Hem';
  menuDiv.appendChild(menuLink);

  if (isMegaMenu) {
    const accordionIcon = document.createElement('i');
    accordionIcon.className = 'fw-icon-down-open fw-accordionToggle';
    menuDiv.appendChild(accordionIcon);
  }

  const subMenu = document.createElement('ul');
  if (isMegaMenu) subMenu.className = 'fw-accordionContent';

  productMenuItem.appendChild(menuDiv);
  productMenuItem.appendChild(subMenu);
  navTree.prepend(productMenuItem);

  // Flytta befintliga produktkategorier
  existingNavItems.forEach((item) => {
    if (item === productMenuItem) return;
    const link = item.querySelector('a, div > a');
    if (!link) return;
    subMenu.appendChild(item);
  });

  // Lägg till undermenysposter från MainMenu (Kampanj, Nyheter, Outlet, etc.)
  // I mobil visas de utan nested underlistor
  addMainMenuProductSubs(subMenu, productsSource, isMegaMenu);
}

/**
 * Lägger till undermenysposter från MainMenu till produkt-submenyn.
 */
function addMainMenuProductSubs(subMenu, productsSource, isMegaMenu = false) {
  const mainMenuProductSubs = productsSource.querySelectorAll(':scope > ul > li');
  if (mainMenuProductSubs.length === 0) return;

  const newSubItemUl = document.createElement('ul');
  newSubItemUl.className = 'wl-main-menu-product-sublist';

  mainMenuProductSubs.forEach((subItem) => {
    const directLink = subItem.querySelector(':scope > a');
    const directSpan = subItem.querySelector(':scope > span.menu-sub-title');
    const innerDiv = subItem.querySelector(':scope > div');
    const divSpan = innerDiv?.querySelector(':scope > span.menu-sub-title');
    const childUl = subItem.querySelector(':scope > ul') || innerDiv?.querySelector(':scope > ul');

    // Early return: inget användbart innehåll
    if (!directLink && !directSpan && !divSpan && !childUl) return;

    const newSubItemLi = document.createElement('li');
    newSubItemLi.className = 'wl-main-menu-product wl-added';

    // Lägg till titel
    if (directSpan || divSpan) {
      const span = document.createElement('span');
      span.className = 'menu-sub-title';
      span.textContent = directSpan?.textContent?.trim() || divSpan?.textContent?.trim();
      newSubItemLi.appendChild(span);
    } else if (directLink) {
      const titleA = document.createElement('a');
      titleA.href = directLink.href;
      titleA.textContent = directLink.textContent.trim();
      newSubItemLi.appendChild(titleA);
    }

    // Lägg till undermeny om den finns – men bara i mega-menyn, inte mobil
    if (isMegaMenu && childUl) {
      const childLinks = childUl.querySelectorAll(':scope > li > a');
      if (childLinks.length > 0) {
        const newSubUl = document.createElement('ul');
        newSubUl.className = childUl.className || 'fw-list-unstyled';

        childLinks.forEach((link) => {
          const newLi = document.createElement('li');
          newLi.className = link.closest('li')?.className || 'level3';
          const newA = document.createElement('a');
          newA.href = link.href;
          newA.textContent = link.textContent.trim();
          newLi.appendChild(newA);
          newSubUl.appendChild(newLi);
        });

        newSubItemLi.appendChild(newSubUl);
      }
    }

    newSubItemUl.appendChild(newSubItemLi);
  });

  if (newSubItemUl.children.length > 0) {
    subMenu.appendChild(newSubItemUl);
  }
}

/**
 * Lägger till övriga MainMenu-poster (utom produktmenyn) i NavTree.
 */
function addOtherMainMenuItems(navTree, mainMenuItems, productsSource, isMegaMenu) {
  mainMenuItems.forEach((item) => {
    if (item === productsSource) return;

    const span = item.querySelector(':scope > span.menu-sub-title');
    const link = item.querySelector(':scope > a');
    const subMenuEl = item.querySelector(':scope > ul');

    // Early return: inget användbart innehåll
    if (!link && !span) return;

    const newItem = document.createElement('li');
    newItem.className = 'wl-main-menu-item wl-added';

    // Enkel länk
    if (link) {
      const newLink = document.createElement('a');
      newLink.href = link.href;
      newLink.textContent = link.textContent.trim();
      newItem.appendChild(newLink);
    }
    // Länk med undermeny
    else if (span) {
      const firstChildLink = subMenuEl?.querySelector('li:first-child > a');

      if (isMegaMenu) {
        const newDiv = document.createElement('div');
        const newLink = document.createElement('a');
        newLink.textContent = span.textContent.trim();
        newLink.href = firstChildLink?.href || '#';
        newDiv.appendChild(newLink);

        const accordionIcon = document.createElement('i');
        accordionIcon.className = 'fw-icon-down-open fw-accordionToggle';
        newDiv.appendChild(accordionIcon);

        newItem.appendChild(newDiv);

        // Lägg till undermeny
        if (subMenuEl) {
          const subItems = [...subMenuEl.querySelectorAll(':scope > li')];
          if (subItems.length > 0) {
            const newSubMenu = document.createElement('ul');
            newSubMenu.className = 'fw-accordionContent';

            subItems.forEach((sub) => {
              const subLink = sub.querySelector('a');
              if (!subLink) return;

              const newSub = document.createElement('li');
              const newA = document.createElement('a');
              newA.href = subLink.href;
              newA.textContent = subLink.textContent.trim();
              newSub.appendChild(newA);
              newSubMenu.appendChild(newSub);
            });

            if (newSubMenu.children.length > 0) {
              newItem.appendChild(newSubMenu);
            }
          }
        }
      } else {
        // Mobil: enkel länk till första barnet (ingen accordion/undermeny)
        const newLink = document.createElement('a');
        newLink.textContent = span.textContent.trim();
        newLink.href = firstChildLink?.href || '#';
        newItem.appendChild(newLink);
      }
    }

    navTree.appendChild(newItem);
  });
}

/**
 * Skapar logout menypost i NavTree.
 * Visas endast när användaren är inloggad.
 * Delad mellan mega menu och mobil meny.
 */
export function createLogoutMenuItem(element, prepend = false, button = false) {
  if (!LOGGED_IN) return;

  const logoutItem = document.createElement('li');
  logoutItem.className = 'wl-logout-menu-item wl-added';

  const lang = document.documentElement.lang || 'sv';

  const logoutLink = document.createElement('a');
  logoutLink.className = 'wl-logout-link wl-added';
  logoutLink.href = `/${lang}/logout`;
  logoutLink.textContent = lang === 'en' ? 'Log out' : 'Logga ut';
  logoutItem.appendChild(logoutLink);

  if (prepend == true) {
    if (button) {
      $(element).prepend(logoutLink);
    } else {
      $(element).prepend(logoutItem);
    }
  } else {
    if (button) {
      $(element).append(logoutLink);
    } else {
      $(element).append(logoutItem);
    }
  }
}

/**
 * Skapar login/mina-sidor menypost i NavTree.
 * Delad mellan mega menu och mobil meny.
 */
export function createLoginMenuItem(navTree) {
  if (navTree.querySelector('.wl-login-menu-item')) return;

  const loginItem = document.createElement('li');
  loginItem.className = 'wl-login-menu-item wl-added';

  const loginLink = document.createElement('a');
  loginLink.className = 'wl-added';

  const icon = document.createElement('i');
  icon.className = 'fw-icon-user';

  const span = document.createElement('span');

  if (LOGGED_IN) {
    loginLink.href = document.querySelector('#MyAccountWrapper .nav-myaccount a')?.getAttribute('href') || '/sv/meny/Mina_sidor/Mina_uppgifter';
    if (document.querySelector('html[lang="en"]')) {
      span.textContent = 'My pages';
    } else {
      span.textContent = 'Mina sidor';
    }
  } else {
    loginLink.href = '/login';
    if (document.querySelector('html[lang="en"]')) {
      span.textContent = 'Login / Sign up';
    } else {
      span.textContent = 'Logga in / Registrera';
    }
  }

  loginLink.appendChild(icon);
  loginLink.appendChild(span);
  loginItem.appendChild(loginLink);
  navTree.appendChild(loginItem);
}

/**
 * Lägger till kategori-ikoner i menyn.
 * Delad mellan mega menu och mobil meny.
 *
 * @param {Element} navTree - NavTree ul-elementet
 * @param {Object} options
 * @param {boolean} options.isMegaMenu - Om true: prepend img till div. Om false: prepend till a-länken.
 */
export function addCategoryIcons(navTree, options = {}) {
  const { isMegaMenu = false } = options;

  const productMenuItem = navTree.querySelector('.wl-products-menu-item');
  if (!productMenuItem) return;

  const productMenu = productMenuItem.querySelector(':scope > ul');
  if (!productMenu) return;

  const categoryItems = productMenu.querySelectorAll(':scope > li');

  categoryItems.forEach((li) => {
    const div = li.querySelector(':scope > div');
    const link = div?.querySelector('a') || li.querySelector(':scope > div > a');
    if (!link) return;

    // Kolla om ikon redan finns
    if (li.querySelector('.wl-category__icon, .wl-menu-icon')) return;

    const menuText = link.textContent.trim();
    const iconPath = getCategoryIcon(menuText);
    if (!iconPath) return;

    const iconImg = document.createElement('img');
    iconImg.className = isMegaMenu ? 'wl-category__icon' : 'wl-menu-icon wl-added';
    iconImg.src = iconPath;
    iconImg.alt = '';
    iconImg.setAttribute('aria-hidden', 'true');

    if (isMegaMenu && div) {
      // Mega menu: prepend till div-containern (bredvid länken)
      div.prepend(iconImg);
    } else {
      // Mobil: prepend inuti a-länken, lägg till product-category klass
      li.classList.add('wl-product-category');
      link.prepend(iconImg);
    }
  });
}
